import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Button,
  CircularProgress,
  Alert,
  Grid,
  Divider,
  TextField,
  IconButton,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@mui/material';
import { Save, Edit } from '@mui/icons-material';
import ProfilePicture from '../components/ProfilePicture';
import api from '../config/api';

const Profile = () => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editing, setEditing] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    phone_number: '',
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  const [emailDialogOpen, setEmailDialogOpen] = useState(false);
  const [pendingSubmitData, setPendingSubmitData] = useState(null);
  
  const navigate = useNavigate();
  const location = useLocation();

  // Check for query params (for any success messages)
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get('discord_linked') === 'true') {
      setSuccess('Your Discord account has been successfully linked!');
      // Clear the URL parameter
      navigate('/profile', { replace: true });
    }
  }, [location, navigate]);

  // Fetch user profile
  useEffect(() => {
    const fetchProfile = async () => {
      try {
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }

        // Get user from localStorage first for faster display
        const storedUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (storedUser && storedUser.email) {
          setUser(storedUser);
          setFormData({
            name: storedUser.name || '',
            email: storedUser.email || '',
            phone_number: storedUser.phone_number || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          setLoading(false); // Stop loading when we have data from localStorage
        }

        // Then fetch the latest from the server
        try {
          const response = await api.get('/auth/profile');
          setUser(response.data);
          setFormData({
            name: response.data.name || '',
            email: response.data.email || '',
            phone_number: response.data.phone_number || '',
            currentPassword: '',
            newPassword: '',
            confirmPassword: ''
          });
          
          // Update localStorage with fresh data
          localStorage.setItem('user', JSON.stringify(response.data));
          setLoading(false);
        } catch (err) {
          // Only set error if we don't already have user data from localStorage
          if (!user) {
            setError('Failed to fetch user profile');
            setLoading(false);
          }
          console.error(err);
        }
      } catch (err) {
        setError('Failed to fetch user profile');
        setLoading(false);
        console.error(err);
      }
    };

    fetchProfile();
  }, [navigate]);

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleProfilePictureUpdate = (newPictureData) => {
    if (!newPictureData) {
      // Handle profile picture removal
      setUser({ ...user, profile_picture_url: null });
      setSuccess('Profile picture removed successfully');
      return;
    }
    
    // Handle profile picture update from new endpoints
    // New ProfilePicture component already handles the API call and localStorage update
    // We just need to update the UI state here
    setUser({ ...user, profile_picture_url: newPictureData });
    setSuccess('Profile picture updated successfully');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Basic field validation
    if (!formData.name || !formData.email) {
      setError('Name and email are required fields');
      return;
    }

    // Validate email format
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(formData.email)) {
      setError('Please enter a valid email address');
      return;
    }

    // Validate password fields if attempting to change password
    if (formData.newPassword || formData.confirmPassword || formData.currentPassword) {
      if (formData.newPassword !== formData.confirmPassword) {
        setError('New passwords do not match');
        return;
      }
      if (!formData.currentPassword) {
        setError('Current password is required to set a new password');
        return;
      }
      
      // Check password strength
      if (formData.newPassword.length < 8) {
        setError('New password must be at least 8 characters long');
        return;
      }
    }

    // Check if email is being changed
    const isEmailChanged = user.email !== formData.email;

    // Prepare the data to submit
    const updateData = {
      name: formData.name,
      email: formData.email,
      phone_number: formData.phone_number || null
    };

    // Add password fields if changing password
    if (formData.newPassword && formData.currentPassword) {
      updateData.currentPassword = formData.currentPassword;
      updateData.newPassword = formData.newPassword;
    }

    // If email is changing, ask for confirmation
    if (isEmailChanged) {
      setPendingSubmitData(updateData);
      setEmailDialogOpen(true);
      return;
    }

    // Otherwise, proceed with the update
    submitProfileUpdate(updateData);
  };

  // New function to handle the actual submission
  const submitProfileUpdate = async (updateData) => {
    try {
      const response = await api.put('/auth/profile', updateData);
      
      // Check if the server returned a new token (email change)
      if (response.data.token) {
        localStorage.setItem('token', response.data.token);
        // Display special message for email change
        setSuccess('Profile updated successfully. Your email has been changed, you may need to log in again.');
      } else {
        setSuccess('Profile updated successfully');
      }

      // Update local user data
      const updatedUser = { ...response.data };
      delete updatedUser.token; // Remove token from user object if it exists
      
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      // Dispatch custom event to notify other components of user data change
      window.dispatchEvent(new Event('userDataChanged'));
      
      setEditing(false);
      
      // Clear password fields
      setFormData({
        ...formData,
        currentPassword: '',
        newPassword: '',
        confirmPassword: ''
      });
    } catch (err) {
      console.error('Profile update error:', err);
      setError(err.response?.data?.error || 'Failed to update profile');
    }
  };

  // Handle dialog responses
  const handleEmailDialogClose = () => {
    setEmailDialogOpen(false);
    setPendingSubmitData(null);
  };

  const handleEmailDialogConfirm = () => {
    if (pendingSubmitData) {
      submitProfileUpdate(pendingSubmitData);
    }
    setEmailDialogOpen(false);
    setPendingSubmitData(null);
  };

  if (loading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  return (
    <Container maxWidth="md" sx={{ mt: 4, pb: 6 }}>
      <Paper elevation={3} sx={{ p: 4 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h4">
            My Profile
          </Typography>
          <IconButton 
            color="primary" 
            onClick={() => setEditing(!editing)}
            sx={{ border: '1px solid', borderColor: 'divider' }}
          >
            <Edit />
          </IconButton>
        </Box>
        
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
        
        {user && (
          <Box component="form" onSubmit={handleSubmit}>
            <Grid container spacing={4}>
              <Grid item xs={12} md={4} sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                <ProfilePicture
                  currentUrl={user.profile_picture_url}
                  onUpdate={handleProfilePictureUpdate}
                  userId={user.email}
                />
                <Typography variant="body2" color="text.secondary" sx={{ mt: 2, textAlign: 'center' }}>
                  Click the camera icon to update your profile picture
                </Typography>
              </Grid>
              
              <Grid item xs={12} md={8}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Full Name"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      disabled={!editing}
                      required
                      helperText={editing ? "Your full name" : ""}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Email Address"
                      name="email"
                      type="email"
                      value={formData.email}
                      onChange={handleChange}
                      disabled={!editing}
                      required
                      helperText={editing ? "This will be your new login email if changed" : ""}
                    />
                  </Grid>
                  
                  <Grid item xs={12}>
                    <TextField
                      fullWidth
                      label="Phone Number"
                      name="phone_number"
                      value={formData.phone_number}
                      onChange={handleChange}
                      disabled={!editing}
                      helperText={editing ? "Optional: Your contact phone number" : ""}
                    />
                  </Grid>

                  {editing && (
                    <>
                      <Grid item xs={12}>
                        <Typography variant="h6" sx={{ mt: 2, mb: 1 }}>
                          Change Password
                        </Typography>
                      </Grid>
                      
                      <Grid item xs={12}>
                        <TextField
                          fullWidth
                          label="Current Password"
                          name="currentPassword"
                          type="password"
                          value={formData.currentPassword}
                          onChange={handleChange}
                          disabled={!editing}
                          helperText="Required to change your password"
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="New Password"
                          name="newPassword"
                          type="password"
                          value={formData.newPassword}
                          onChange={handleChange}
                          disabled={!editing}
                          helperText="Minimum 8 characters"
                        />
                      </Grid>
                      
                      <Grid item xs={12} sm={6}>
                        <TextField
                          fullWidth
                          label="Confirm New Password"
                          name="confirmPassword"
                          type="password"
                          value={formData.confirmPassword}
                          onChange={handleChange}
                          disabled={!editing}
                          helperText="Re-enter your new password"
                        />
                      </Grid>
                    </>
                  )}
                </Grid>
                
                {editing && (
                  <Box sx={{ mt: 3, display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="contained"
                      color="primary"
                      type="submit"
                      startIcon={<Save />}
                    >
                      Save Changes
                    </Button>
                  </Box>
                )}
              </Grid>
            </Grid>
          </Box>
        )}
      </Paper>

      {/* Email change confirmation dialog */}
      <Dialog
        open={emailDialogOpen}
        onClose={handleEmailDialogClose}
        aria-labelledby="alert-dialog-title"
        aria-describedby="alert-dialog-description"
      >
        <DialogTitle id="alert-dialog-title">
          Confirm Email Address Change
        </DialogTitle>
        <DialogContent>
          <DialogContentText id="alert-dialog-description">
            You are about to change your email address from <strong>{user?.email}</strong> to <strong>{formData.email}</strong>. 
            This will affect how you log in to your account. Are you sure you want to proceed?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleEmailDialogClose} color="primary">
            Cancel
          </Button>
          <Button onClick={handleEmailDialogConfirm} color="primary" autoFocus>
            Confirm
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default Profile; 